import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, PenLine, BookOpen, Users, Trophy, 
  MessageSquare, Settings, ChevronDown, ChevronUp,
  Shield, Eye, Save, Search
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

const faqSections = [
  {
    title: "Getting Started",
    icon: PenLine,
    items: [
      {
        q: "How do I publish my first work?",
        a: "Click the \"Write\" button in the top navigation bar. Write your title, summary, and content. Before publishing, scroll down and check the \"I confirm this is my original work\" box. Then click the \"Publish\" button in the top-right corner. You can also save as a draft first and publish later from the \"My Works\" page."
      },
      {
        q: "How do I change the genre of my work?",
        a: "While in the editor, click the \"Settings\" gear icon in the top bar. This opens a panel where you can change the genre, add tags, change fonts, upload a cover photo, and invite collaborators."
      },
      {
        q: "Can I save my work as a draft?",
        a: "Yes! Click \"Save Draft\" in the editor header. Your work will be saved but not visible to others until you click \"Publish\". You can find all your drafts on the \"My Works\" page."
      },
      {
        q: "How do I edit a work I've already published?",
        a: "Go to \"My Works\" from the user menu (click your avatar in the top-right). Find the work you want to edit and click the edit icon. Make your changes and save."
      }
    ]
  },
  {
    title: "Exploring & Reading",
    icon: BookOpen,
    items: [
      {
        q: "How do I find works to read?",
        a: "Use the \"Sections\" page to browse by genre, or the \"Works\" page to see all published works. You can also use \"Discover\" to find writers and the works they've shared."
      },
      {
        q: "How do I leave a comment on someone's work?",
        a: "Open the work you'd like to comment on and scroll to the bottom. Type your comment in the comment box and click send. You can @mention other writers by typing @ followed by their username."
      },
      {
        q: "What is the Reading List?",
        a: "Your Reading List is a personal collection of works you want to read later. When browsing, click the bookmark icon on any work to add it to your Reading List. Access it from the user menu."
      }
    ]
  },
  {
    title: "Groups & Community",
    icon: Users,
    items: [
      {
        q: "How do I join a writing group?",
        a: "Go to the \"Groups\" page from the main navigation. Browse available groups and click \"Join Group\" on any group that interests you. Once you're a member, you can post discussions and reply to others."
      },
      {
        q: "How do I create a new group?",
        a: "On the \"Groups\" page, click \"Create Group\". Fill in the group name, description, and optional genre focus. You'll automatically be the group's creator and first member."
      },
      {
        q: "How do I reply to a discussion in a group?",
        a: "Open the group, then click on any discussion post to open it. You'll see the full post with replies below it, and a reply box at the bottom. Type your reply and click send. You must be a group member to reply."
      },
      {
        q: "How do I delete a group I created?",
        a: "Open the group you created. You'll see a \"Delete Group\" button next to the \"Leave Group\" button. Click it and confirm. This will permanently delete the group and all its posts."
      }
    ]
  },
  {
    title: "Challenges",
    icon: Trophy,
    items: [
      {
        q: "How do writing challenges work?",
        a: "Admins create challenges with prompts, deadlines, and sometimes genre restrictions. To participate, go to the challenge page and click \"Enter Challenge\". Write a story inspired by the prompt and submit it before the deadline. Judges will score entries and results will be announced."
      },
      {
        q: "How do I enter a challenge?",
        a: "Go to \"Challenges\" in the navigation. Find an active challenge and click on it. Read the prompt, then click \"Enter Challenge\" to write your submission. You can link an existing work or create a new one."
      }
    ]
  },
  {
    title: "Profile & Settings",
    icon: Settings,
    items: [
      {
        q: "How do I update my profile?",
        a: "Click your avatar in the top-right corner and select \"Settings\". Here you can update your display name, bio, avatar, and other profile information."
      },
      {
        q: "How do I see my achievements?",
        a: "Click your avatar and select \"Achievements\" from the dropdown menu. This shows badges and milestones you've earned through your writing activity."
      },
      {
        q: "Can I change my password?",
        a: "If you're logged in, go to Settings from the user menu. If you're locked out, use the \"Forgot Password\" link on the login page to receive a reset email."
      }
    ]
  },
  {
    title: "Important Policies",
    icon: Shield,
    items: [
      {
        q: "Who owns the work I post here?",
        a: "You own 100% of your work. Collectively Write does not claim any rights to your writing. This platform is your portfolio."
      },
      {
        q: "Is AI-generated content allowed?",
        a: "No. Collectively Write is a community for real writers sharing real craft. All submissions must be original writing — no AI-generated content. You must confirm this before publishing."
      },
      {
        q: "What are the community guidelines?",
        a: "We maintain a respectful, supportive environment for writers. Read our full Community Guidelines for details on content rules, respectful interaction, and consequences for violations."
      }
    ]
  }
];

const FAQItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div 
      className="border-b border-brand-primary/10 last:border-b-0"
      data-testid="faq-item"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-2 text-left hover:bg-brand-primary/5 rounded-lg transition-colors"
        data-testid="faq-question"
      >
        <span className="font-medium text-text-primary pr-4">{item.q}</span>
        {open ? <ChevronUp className="w-4 h-4 text-text-muted flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-text-muted flex-shrink-0" />}
      </button>
      {open && (
        <div className="pb-4 px-2 text-text-secondary leading-relaxed text-sm animate-in fade-in duration-200">
          {item.a}
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="faq-page">
      {/* Header */}
      <div className="text-center mb-12">
        <HelpCircle className="w-12 h-12 text-brand-accent mx-auto mb-4" />
        <h1 className="font-serif text-4xl font-semibold text-text-primary mb-3">
          How to Navigate the Platform
        </h1>
        <p className="text-text-secondary max-w-2xl mx-auto">
          New here? This guide will help you find your way around Collectively Write. 
          From publishing your first work to joining community groups, everything you need to know is below.
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-12">
        {faqSections.map((section) => (
          <a
            key={section.title}
            href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-2 p-3 rounded-lg bg-white border border-brand-primary/10 hover:border-brand-accent/30 hover:bg-brand-accent/5 transition-colors"
          >
            <section.icon className="w-4 h-4 text-brand-accent" />
            <span className="text-sm font-medium text-text-primary">{section.title}</span>
          </a>
        ))}
      </div>

      {/* FAQ Sections */}
      <div className="space-y-8">
        {faqSections.map((section) => (
          <Card key={section.title} className="bg-white border-brand-primary/10" id={section.title.toLowerCase().replace(/\s+/g, '-')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <section.icon className="w-5 h-5 text-brand-accent" />
                <h2 className="font-serif text-xl font-semibold text-text-primary">{section.title}</h2>
              </div>
              <div>
                {section.items.map((item) => (
                  <FAQItem key={item.q} item={item} />
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Still need help? */}
      <div className="text-center mt-12 p-8 bg-white rounded-xl border border-brand-primary/10">
        <p className="text-text-secondary mb-3">
          Still have questions? Reach out to an admin or check the{' '}
          <Link to="/guidelines" className="text-brand-accent hover:underline">Community Guidelines</Link>.
        </p>
      </div>
    </div>
  );
};

export default FAQPage;
